import { useUnit } from 'effector-react'
import isNil from 'lodash/isNil'

import IconExternal from '@/assets/icons/icon-external.svg'
import {
  DateRangeContextKey,
  useDateRangeContext,
} from '@/components/provider/date-range-provider.tsx'
import { Avatar, AvatarImage } from '@/components/ui/avatar.tsx'
import { $creatorId } from '@/models/creators.ts'
import { $socialViewSortOptions } from '@/modules/statistics/social/data/models.ts'
import { useSocialMediaType } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'
import { PerformanceStateIndicator } from '@/modules/statistics/social/ui/PerformanceStateIndicator.tsx'
import { formatNumberWithCommas } from '@/utils/helpers'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import { useSocialPagination } from '../hooks/use-video-pagination'
import type { PerformanceStateInfo, SocialAccount } from '../types'
import { VideoCarousel } from './Carousel'

interface SocialAccountWidgetProps {
  account: SocialAccount
  performanceState?: PerformanceStateInfo
}

/**
 * The component dedicated to the tiktok account. It renders basic stats and a
 * carousel list that contains all tiktok clips
 */
export const SocialAccountWidget = ({
  account,
  performanceState,
}: SocialAccountWidgetProps) => {
  const creatorId = useUnit($creatorId)

  const { date } = useDateRangeContext(DateRangeContextKey.Social)

  const { socialMediaType, accountLink } = useSocialMediaType()
  const sortOption = useUnit($socialViewSortOptions)

  const { videos, handleLoadMore, isFetchingNextPage } = useSocialPagination({
    account: account,
    socialMediaType: socialMediaType,
    creatorId: creatorId!,
    dateRange: date,
    sortOption,
  })

  const videosElements = videos?.pages
    .flatMap((page) => page.views)
    .filter(isTruthyAndNotEmpty)

  return (
    <div className="flex flex-col gap-6 rounded-lg bg-card px-6 py-4">
      <div className="flex flex-col items-center gap-6">
        <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
          <div className="flex gap-2 truncate">
            <Avatar className="h-[52px] w-[52px]">
              <AvatarImage src={account.avatarUrl!} />
            </Avatar>
            <div className="flex flex-col">
              <a
                className="text-primary-default flex items-center gap-2 self-center text-base font-medium text-title"
                href={`${accountLink}${account.account}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>@{account.account}</span>
                <IconExternal className="text-primary-500" />
              </a>
              {performanceState && (
                <PerformanceStateIndicator
                  performanceState={performanceState}
                />
              )}
            </div>
          </div>

          <div className="flex flex-row gap-8">
            {!isNil(account.totalUploads) && (
              <div className="flex flex-col items-center">
                <span className="text-base font-semibold text-title">
                  {formatNumberWithCommas(account.totalUploads)}
                </span>
                <span className="text-sm font-medium text-secondary-200">
                  Uploads
                </span>
              </div>
            )}

            <div className="flex flex-col">
              <span className="text-base font-semibold text-title">
                {account.viewsGrowth >= 0 ? '+' : ''}
                {formatNumberWithCommas(account.viewsGrowth)}
              </span>
              <span className="text-sm font-medium text-secondary-200">
                Views
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-base font-semibold text-title">
                {account.followersGrowth >= 0 ? '+' : ''}
                {formatNumberWithCommas(account.followersGrowth)}
              </span>
              <span className="text-sm font-medium text-secondary-200">
                Followers
              </span>
            </div>
          </div>
        </div>
      </div>

      <div>
        {videosElements && (
          <VideoCarousel
            items={videosElements}
            onLoadMore={handleLoadMore}
            itemsLoading={isFetchingNextPage}
          />
        )}
        {!videosElements && (
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="h-[500px] w-[300px] animate-pulse rounded-lg bg-skeleton " />
            <div className="h-[500px] w-[300px] animate-pulse rounded-lg bg-skeleton " />
            <div className="h-[500px] w-[300px] animate-pulse rounded-lg bg-skeleton " />
          </div>
        )}
      </div>
    </div>
  )
}
